/**
 * LogExplorerPage — 统一日志 / 追踪浏览器
 *
 * Tabs:
 * 1. API 日志     — GET /api/v1/logs/api
 * 2. AI 日志      — GET /api/v1/logs/ai
 * 3. 调试历史     — GET /api/v1/logs/debug
 * 4. 执行追踪     — GET /api/v1/logs/traces/exec
 * 5. 决策追踪     — GET /api/v1/logs/traces/dec
 * 6. 慢查询       — GET /api/v1/logs/slow-queries
 * 7. 日志摘要     — GET /api/v1/logs/summary
 */

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FileText,
  RefreshCw,
  Search,
  Loader2,
  AlertTriangle,
  ArrowUpDown,
  Clock,
  Brain,
  Bug,
  Zap,
  BarChart3,
  Cpu,
  ChevronLeft,
  ChevronRight,
  Copy,
  Link2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ─────────────────────────────────────

type TabId = 'api' | 'ai' | 'debug' | 'exec-trace' | 'dec-trace' | 'slow-query' | 'summary';

// ─── Trace Badge ──────────────────────────────
function TraceBadge({ val }: { val: unknown }) {
  const id = val ? String(val) : null;
  if (!id) return <span className="text-muted-foreground">—</span>;
  const short = id.slice(0, 8);
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[10px] bg-violet-50 text-violet-700 border border-violet-200 rounded px-1 py-px">
      {short}
      <button
        title={id}
        className="opacity-50 hover:opacity-100 transition-opacity"
        onClick={() => void navigator.clipboard.writeText(id)}
      >
        <Copy className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

interface TabDef {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  endpoint: string;
}

const TABS: TabDef[] = [
  { id: 'api', label: 'API Logs', icon: <FileText className="h-3.5 w-3.5" />, endpoint: '/logs/api' },
  { id: 'ai', label: 'AI Logs', icon: <Brain className="h-3.5 w-3.5" />, endpoint: '/logs/ai' },
  { id: 'debug', label: 'Debug History', icon: <Bug className="h-3.5 w-3.5" />, endpoint: '/logs/debug' },
  { id: 'exec-trace', label: 'Exec Traces', icon: <Zap className="h-3.5 w-3.5" />, endpoint: '/logs/traces/exec' },
  { id: 'dec-trace', label: 'Decision Traces', icon: <ArrowUpDown className="h-3.5 w-3.5" />, endpoint: '/logs/traces/dec' },
  { id: 'slow-query', label: 'Slow Queries', icon: <Clock className="h-3.5 w-3.5" />, endpoint: '/logs/slow-queries' },
  { id: 'summary', label: 'Summary', icon: <BarChart3 className="h-3.5 w-3.5" />, endpoint: '/logs/summary' },
];

interface LogEntry {
  id: string;
  [key: string]: unknown;
}

interface LogSummary {
  counts: {
    apiLogs: number;
    aiLogs: number;
    debugHistory: number;
    executionTraces: number;
    decisionTraces: number;
    slowQueries: number;
    skillExecutions: number;
  };
  ai: { totalCostUsd: number; totalPromptTokens: number; totalCompletionTokens: number; avgDurationMs: number };
  api: { avgDurationMs: number; maxDurationMs: number; p95DurationMs: number };
  topErrors: Array<{ error_code: string; count: number }>;
}

// ─── Tenant GUID → Short Name ─────────────────
const TENANT_SHORT: Record<string, string> = {
  '00000000-0000-4000-a000-000000000001': 'LCH',
  'e8271486-0226-4b27-a00d-14bcce9b56cf': 'PNS',
};
function shortTenant(val: unknown): React.ReactNode {
  const id = String(val ?? '');
  return <span title={id}>{TENANT_SHORT[id] ?? id.slice(0, 8)}</span>;
}
function shortId(val: unknown): React.ReactNode {
  const id = String(val ?? '-');
  if (id === '-' || id.length < 12) return <span>{id}</span>;
  return <span title={id} className="font-mono">{id.slice(0, 8)}…</span>;
}

// ─── Page Component ────────────────────────────

export default function LogExplorerPage() {
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<LogEntry[]>([]);
  const [summary, setSummary] = useState<LogSummary | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [traceFilter, setTraceFilter] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  const fetchTab = useCallback(
    async (tabId: TabId, pageNum: number, traceId?: string) => {
      setLoading(true);
      try {
        const tab = TABS.find((t) => t.id === tabId)!;
        if (tabId === 'summary') {
          const data = await api.get<LogSummary>(tab.endpoint);
          setSummary(data);
          setRows([]);
        } else {
          const params = new URLSearchParams({ limit: String(PAGE_SIZE), page: String(pageNum) });
          if (tabId === 'ai' && traceId) params.set('trace_id', traceId);
          const data = await api.get<{ items: LogEntry[] }>(tab.endpoint + '?' + params.toString());
          setRows(data.items ?? []);
          setSummary(null);
        }
      } catch (err) {
        console.error(`Failed to fetch ${tabId}:`, err);
        setRows([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void fetchTab(activeTab, page, traceFilter || undefined);
  }, [activeTab, page, fetchTab]);

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    setPage(1);
    setSearchQuery('');
    setTraceFilter('');
  };

  const handleTraceSearch = () => {
    setPage(1);
    void fetchTab(activeTab, 1, traceFilter || undefined);
  };

  const filteredRows = searchQuery
    ? rows.filter((r) =>
        JSON.stringify(r).toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : rows;

  return (
    <div className="space-y-4 p-6">
      {/* ─── Header ───────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6 text-indigo-500" />
            Log Explorer
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            API Logs · AI Inference · Exec Traces · Decision Audit · Slow Queries
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void fetchTab(activeTab, page, traceFilter || undefined)}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* ─── Tabs ─────────────────────────── */}
      <div className="flex flex-wrap gap-1 border-b pb-2">
        {TABS.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            size="sm"
            className="text-xs"
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.icon}
            <span className="ml-1">{tab.label}</span>
          </Button>
        ))}
      </div>

      {/* ─── Content ──────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center h-[40vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : activeTab === 'summary' && summary ? (
        <SummaryView summary={summary} />
      ) : (
        <>
          {/* Search row */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative max-w-xs flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search logs…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {/* Trace ID filter — only shown for AI Logs tab */}
            {activeTab === 'ai' && (
              <div className="flex items-center gap-1.5">
                <div className="relative">
                  <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-violet-500" />
                  <Input
                    placeholder="Filter by trace ID…"
                    value={traceFilter}
                    onChange={(e) => setTraceFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleTraceSearch()}
                    className="pl-8 w-64 text-xs font-mono"
                  />
                </div>
                <Button size="sm" variant="secondary" onClick={handleTraceSearch} className="text-xs">
                  Filter
                </Button>
                {traceFilter && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setTraceFilter(''); setPage(1); void fetchTab(activeTab, 1, undefined); }}
                    className="text-xs text-muted-foreground"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Table */}
          <LogTable rows={filteredRows} tabId={activeTab} />

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Page {page} · {filteredRows.length} entries
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={rows.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Summary View ──────────────────────────────

function SummaryView({ summary }: { summary: LogSummary }) {
  const c = summary.counts;
  const items = [
    { label: 'API Logs', count: c.apiLogs, color: 'text-primary' },
    { label: 'AI Inference Logs', count: c.aiLogs, color: 'text-purple-600' },
    { label: 'Debug Sessions', count: c.debugHistory, color: 'text-yellow-600' },
    { label: 'Exec Traces', count: c.executionTraces, color: 'text-green-600' },
    { label: 'Decision Traces', count: c.decisionTraces, color: 'text-cyan-600' },
    { label: 'Slow Queries', count: c.slowQueries, color: 'text-red-600' },
    { label: 'Skill Executions', count: c.skillExecutions, color: 'text-orange-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Counts Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((item) => (
          <Card key={item.label}>
            <CardContent className="py-3">
              <p className="text-[10px] text-muted-foreground">{item.label}</p>
              <p className={cn('text-2xl font-bold', item.color)}>{(item.count ?? 0).toLocaleString()}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Top Errors */}
      {/* AI & API Performance */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="py-3"><p className="text-[10px] text-muted-foreground">AI Cost (24h)</p><p className="text-lg font-bold text-purple-600">${summary.ai.totalCostUsd.toFixed(4)}</p></CardContent></Card>
        <Card><CardContent className="py-3"><p className="text-[10px] text-muted-foreground">AI Tokens</p><p className="text-lg font-bold text-purple-600">{(summary.ai.totalPromptTokens + summary.ai.totalCompletionTokens).toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="py-3"><p className="text-[10px] text-muted-foreground">API Avg</p><p className="text-lg font-bold text-primary">{summary.api.avgDurationMs}ms</p></CardContent></Card>
        <Card><CardContent className="py-3"><p className="text-[10px] text-muted-foreground">API P95</p><p className="text-lg font-bold text-primary">{summary.api.p95DurationMs}ms</p></CardContent></Card>
      </div>

      {summary.topErrors && summary.topErrors.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Top Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {summary.topErrors.map((err, i) => (
                <div key={i} className="flex items-start justify-between text-xs py-1 border-b last:border-b-0">
                  <span className="text-muted-foreground break-all pr-4">{err.error_code}</span>
                  <Badge variant="destructive" className="text-[10px] shrink-0">{err.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Log Table ─────────────────────────────────

function LogTable({ rows, tabId }: { rows: LogEntry[]; tabId: TabId }) {
  if (rows.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm">
        No log entries
      </div>
    );
  }

  // Determine columns based on tab
  const columns = getColumnsForTab(tabId);

  return (
    <div className="overflow-x-auto rounded border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="text-left px-3 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.id ?? idx} className="border-t hover:bg-muted/30">
              {columns.map((col) => (
                <td key={col.key} className="px-3 py-2 text-xs whitespace-nowrap max-w-[260px] overflow-hidden text-ellipsis">
                  {col.render ? col.render(row[col.key]) : String(row[col.key] ?? '-')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Column Definitions Per Tab ────────────────

interface ColumnDef {
  key: string;
  label: string;
  render?: (val: unknown) => React.ReactNode;
}

function statusBadge(val: unknown) {
  const s = Number(val);
  if (s >= 200 && s < 300) return <Badge className="text-[10px] bg-green-100 text-green-800">{s}</Badge>;
  if (s >= 400 && s < 500) return <Badge className="text-[10px] bg-yellow-100 text-yellow-800">{s}</Badge>;
  if (s >= 500) return <Badge className="text-[10px] bg-red-100 text-red-800">{s}</Badge>;
  return <span>{String(val ?? '-')}</span>;
}

function msBadge(val: unknown) {
  const ms = Number(val);
  const color = ms > 5000 ? 'text-red-600' : ms > 1000 ? 'text-yellow-600' : 'text-green-600';
  return <span className={cn('font-mono', color)}>{ms}ms</span>;
}

function timestampCell(val: unknown) {
  if (!val || typeof val !== 'string') return <span>-</span>;
  try {
    return <span className="font-mono">{new Date(val).toLocaleString('en-SG', { hour12: false })}</span>;
  } catch {
    return <span>{String(val)}</span>;
  }
}

function getColumnsForTab(tabId: TabId): ColumnDef[] {
  switch (tabId) {
    case 'api':
      return [
        { key: 'created_at', label: 'Time', render: timestampCell },
        { key: 'tenant_id', label: 'Tenant', render: shortTenant },
        { key: 'method', label: 'Method' },
        { key: 'path', label: 'Path' },
        { key: 'status_code', label: 'Status', render: statusBadge },
        { key: 'duration_ms', label: 'Duration', render: msBadge },
      ];
    case 'ai':
      return [
        { key: 'created_at', label: 'Time', render: timestampCell },
        { key: 'tenant_id', label: 'Tenant', render: shortTenant },
        { key: 'trace_id', label: 'Trace', render: (v) => <TraceBadge val={v} /> },
        { key: 'skill_id', label: 'Skill', render: (v) => v ? <span className="text-xs text-cyan-700 font-mono">{String(v)}</span> : <span className="text-muted-foreground">—</span> },
        { key: 'provider', label: 'Provider' },
        { key: 'model', label: 'Model' },
        { key: 'prompt_tokens', label: 'In Tok' },
        { key: 'completion_tokens', label: 'Out Tok' },
        { key: 'duration_ms', label: 'Duration', render: msBadge },
        { key: 'cost_usd', label: 'Cost', render: (v) => <span className="font-mono">${Number(v ?? 0).toFixed(4)}</span> },
      ];
    case 'debug':
      return [
        { key: 'created_at', label: 'Time', render: timestampCell },
        { key: 'tenant_id', label: 'Tenant', render: shortTenant },
        { key: 'level', label: 'Level' },
        { key: 'skill_id', label: 'Skill' },
        { key: 'error_code', label: 'Error' },
        { key: 'message', label: 'Message' },
      ];
    case 'exec-trace':
      return [
        { key: 'created_at', label: 'Time', render: timestampCell },
        { key: 'tenant_id', label: 'Tenant', render: shortTenant },
        { key: 'workflow_name', label: 'Workflow' },
        { key: 'tool_name', label: 'Tool' },
        { key: 'status', label: 'Status' },
        { key: 'duration_ms', label: 'Duration', render: msBadge },
      ];
    case 'dec-trace':
      return [
        { key: 'created_at', label: 'Time', render: timestampCell },
        { key: 'tenant_id', label: 'Tenant', render: shortTenant },
        { key: 'decision_type', label: 'Decision' },
        { key: 'policy_name', label: 'Policy' },
        { key: 'outcome', label: 'Outcome' },
        { key: 'approver_kind', label: 'Approver' },
      ];
    case 'slow-query':
      return [
        { key: 'created_at', label: 'Time', render: timestampCell },
        { key: 'tenant_id', label: 'Tenant', render: shortTenant },
        { key: 'query', label: 'Query' },
        { key: 'duration_ms', label: 'Duration', render: msBadge },
        { key: 'db_name', label: 'Database' },
      ];
    default:
      return [{ key: 'id', label: 'ID' }];
  }
}
